import {
  Container,
  Button,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LoadingSpinner,
  ModalMessage,
  ProductForm,
} from "../../components"; // form validation schema (with Yup)
import { getStorageToken, useLogout } from "../../utilities";
import { useSelector } from "react-redux";
import {
  adminCreateProductApi,
  adminDeleteProductApi, 
  adminUpdateProductApi,
  adminFetchProductApi,
  adminAddImagesApi,
  adminDeleteImagesApi,
} from "../../api/productsApi";
import { fetchAllAttributesApi } from "../../api/attributesApi";
import { deleteCloudinaryImagesApi, uploadCloudinaryImagesApi } from "../../api/cloudinaryApi";
import { successMsgDuration } from "../../constants/config";

export default function AdminCreateEditProduct(props) {
  // Create/Edit Product Form with validation (formik + Yup libraries)
  // state of input field is stored in Formik's 'values' object
  // handleChange is supplied by Formik, updates the state (values)
  // newAttribute: not managed by formik/yup, own state management
  // props
  //    edit (Boolean): if true, edit product not create
  // if this site is called with id parameters -> open in EDIT mode, else in CREATE mode
  // if open in edit mode, retrieve product data from DB and update initial values in form once fetched
  const { id } = useParams();
  const logout = useLogout();
  const { stayLoggedIn } = useSelector((state) => state.user);
  const navigate = useNavigate(); 
  const availableCategories = useSelector((state) => state.categories);

  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [productData, setProductData] = useState(null);
  const [deletedImagesPublicIds, setDeletedImagesPublicIds] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [availableAttributes, setAvailableAttributes] = useState([]);
  const [showAlertMsgCancel, setShowAlertMsgCancel] = useState(false);
  const [showAlertMsgDeletion, setShowAlertMsgDeletion] = useState(false);

  useEffect(() => {
    const productsAbortController = new AbortController();
    const attributesAbortController = new AbortController();

    const fetchProduct = async () => {
      //json with category, attributes populated
      setIsLoading(true);
      try {
        const token = getStorageToken(stayLoggedIn);
        const res = await adminFetchProductApi(
          token,
          id,
          productsAbortController.signal
        );
        return res;
      } catch (error) {
        setErrorMsg("Could not load product data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    const fetchAvailableAttributes = async () => {
      setIsLoading(true);
      try { 
        const token = getStorageToken(stayLoggedIn);
        const res = await fetchAllAttributesApi(token,
          attributesAbortController.signal
        );
        return res;
      } catch (error) {
        setErrorMsg("Could not load attributes data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };  

    //fetch product with given id from DB when in edit mode
    if (props?.edit && id) {
      //id passed in for edit
      fetchProduct()
        .then((res) => {
          setProductData(res);
        })
        .catch((error) =>
          setErrorMsg("Could not load product data. Please try again later")
        );
    } 
      fetchAvailableAttributes()
        .then((res) => {setAvailableAttributes(res)})
        .catch((error) =>
          setErrorMsg("Could not load attributes data. Please try again later.")
        );
        return () => {
          attributesAbortController.abort();
          productsAbortController.abort();
        }
 }, [props?.edit, id, stayLoggedIn]);


  const handleImageUpload = async (productId, token=null) => {
    //when creating new product/updating product, do image upload in background
    try { 
      if(!token){
        token = getStorageToken(stayLoggedIn);
      }
      // get array of images {url:, public_id:}
      const abortController = new AbortController();
      const uploadedImages = await uploadCloudinaryImagesApi(
        token,
        newImages,
        abortController.signal
      );
      await adminAddImagesApi(token, productId, {images: uploadedImages});
      setNewImages([]);
    } catch (error) {
      setErrorMsg("Image upload failed. Please try again later, or contact our support team for further assistance.");
    }
  };

  const deleteUploadedImages = async() => {
    // deletedImagesPublicIds
    if(!deletedImagesPublicIds || deletedImagesPublicIds.length < 1){
      return;
    }
    try {
      const token = getStorageToken(stayLoggedIn); 
      await deleteCloudinaryImagesApi(token, deletedImagesPublicIds);
      setDeletedImagesPublicIds([])
      await adminDeleteImagesApi(token, id, deletedImagesPublicIds)
    } catch(error){
      setErrorMsg("Images could not be deleted from the server. Please contact our support team for assistance.")
    }
  }

  const handleSaveProduct = async (productData, images) => {
    try {
      const token = getStorageToken(stayLoggedIn);
      const abortController = new AbortController();
      const res = await adminCreateProductApi(token, productData, abortController.signal);
      handleImageUpload(res._id, token);
      await new Promise((resolve) => setTimeout(resolve, successMsgDuration));
      navigate(`/admin/products-list`, {replace: true});
    } catch (error) {
      let errMsg = "Could not save product. Please try again later.";
      if (error?.status === 401) {
        //unautorized, eg token expired
        logout();
      } else if(error?.status === 409) {
        errMsg = error?.message || "A product with this name already exists."; 
      }
      setErrorMsg(errMsg);
    }
  };

  const handleSaveChanges = async (formData) => {
    try {
      const token = getStorageToken(stayLoggedIn);
      const abortController = new AbortController();
      await adminUpdateProductApi(
        token, 
        id,
        formData,
        abortController.signal
      );
      deleteUploadedImages();
      handleImageUpload(id, token);
      await new Promise((resolve) => setTimeout(resolve, successMsgDuration));
      navigate(`/admin/products-list`, { replace: true });
    } catch (error) {
      if (error?.status === 401) {
        //unautorized, eg token expired
        logout();
      }
      setErrorMsg("Could not save product. Please try again later.");
    }
  };

  const handleDeleteImage = (image) => {
    if(image?.public_id){
      // add to cloudinary images to be deleted
      setDeletedImagesPublicIds([...deletedImagesPublicIds, image.public_id])
    } else {
      // not yet on cloudinary -> delete from newImages state variable 
      setNewImages(newImages.filter((img) => img !== image));
    }
  }

  const handleAddImages = (images) => {
    setNewImages([...newImages, ...images]);
  }



  const handleConfirmDeletion = async () => {
      try {
        const token = getStorageToken(stayLoggedIn);
        const abortController = new AbortController();
        const {images} = await adminDeleteProductApi(
          token,
          id,
          abortController.signal
        );
        try{
          // delete images from cloudinary in background
          const publicIds = images.map((image) => image.public_id)
          deleteCloudinaryImagesApi(token, publicIds);
        } catch(error){
          console.log(error);
        }
        navigate(`/admin/products-list`, { replace: true });
      } catch (error) {
        if (error?.status === 401) {
          //unautorized, eg token expired
          logout();
        }
        setErrorMsg(
          "The product could not be deleted. Please try again later. "
        );
      } 
  };

  const handleConfirmCancellation = async () => {
    // ask user if they want to save their changes
    // navigate back to the products list
      navigate("/admin/products-list");
  };

  return (
    <Container className="mt-5">
      {errorMsg && (
        <ModalMessage
          type="danger"
          message={errorMsg}
          setErrorMsg={setErrorMsg}
        />
      )}
          <Button variant="secondary" onClick={() => setShowAlertMsgCancel(true)}>
            <i className="bi bi-arrow-return-left"></i> Products List
          </Button>
          <h1 className="mt-5">{props.edit ? "Edit " : "Add "} Product</h1>
          {isLoading ? <LoadingSpinner /> : null}
          {(productData || !props.edit) &&
            availableAttributes &&
            availableCategories && (
              <ProductForm
                edit={props.edit} 
                productData={productData}
                availableAttributes={availableAttributes}
                availableCategories={availableCategories}
                handleDeleteProduct={() => {
                  if(props.edit){
                    setShowAlertMsgDeletion(true)}
                }}
                handleSave={props.edit ? handleSaveChanges : handleSaveProduct}
                handleCancel={() => { 
                  if(!props.edit) {
                    setShowAlertMsgCancel(true)
                  } }}
                handleAddImages = {handleAddImages}
                handleDeleteImage = {handleDeleteImage}
              />
            )}
    {/* alert message */}
      {showAlertMsgDeletion && 
      <ModalMessage
          message="Are you sure you want to delete this product?" 
          // setErrorMsg={setShowLogoutMsg}
          confirmButton={true}
          handleConfirm={handleConfirmDeletion}
          setShowAlertMsg={setShowAlertMsgDeletion}
        />
      }
          {/* alert message */}
          {showAlertMsgCancel && 
      <ModalMessage
          message= {"Your " + (props.edit ? "changes have" : "product has") +
          " not been saved, yet. Are you sure you want to leave this page?"}
          // setErrorMsg={setShowLogoutMsg}
          confirmButton={true}
          handleConfirm={handleConfirmCancellation}
          setShowAlertMsg={setShowAlertMsgCancel}
        />
      }
    </Container>
  );
}
